import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { SingleValue } from "react-select";
import useI18n from "../../../hooks/useTranslations";
import {
  AbsorberForSettings,
  HandleForSettings,
  InstallationTypeForSettings,
  IntermediateElementForSettings,
  LadderReinforcementForSettings,
  LowerElementForSettings,
  ShuttleForSettings,
  SteelVersionForSettings,
  SystemTypeForSettings,
  UpperExtensionForSettings,
  VertigripSettings,
  WallMaterialFixingForSettings,
  WallMaterialForSettings,
} from "../../../http/vertigrip/types/vertigripSettings";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import { ItemsHelpModal } from "../../ui/ItemsHelpModal";
import LoadingSpinner from "../../ui/LoadingSpinner";
import Numeric from "../../ui/Numeric";
import SectionCard from "../../ui/SectionCard";
import Sections from "../../ui/Sections";
import { BooleanSelectValue, getBooleanValues } from "../../ui/select/BooleanValues";
import { CustomSelect } from "../../ui/select/CustomSelect";
import SimpleTextModalDialog from "../SimpleTextModalDialog";
import styles from "./VertigripSupports.module.css";
import { WallMaterialFixingsHelpModal } from "./WallMaterialFixingsHelpModal";
import { WallMaterialsHelpModal } from "./WallMaterialsHelpModal";

export interface BaseHeightOption {
  value: number;
}

export interface VertigripLowerFixationFormBody {
  baseHeight?: BaseHeightOption;
  installationType: InstallationTypeForSettings | null;
  lowerElement: LowerElementForSettings | null;
  wallMaterial: WallMaterialForSettings | null;
  noWallMaterial: boolean;
  fixing: WallMaterialFixingForSettings | null;
  absorber: AbsorberForSettings | null;
}

export interface VertigripUpperFixationFormBody {
  installationType: InstallationTypeForSettings | null;
  upperExtension: UpperExtensionForSettings | null;
  handle: HandleForSettings | null;
  ladderReinforcement: LadderReinforcementForSettings | null;
  wallMaterial: WallMaterialForSettings | null;
  fixing: WallMaterialFixingForSettings | null;
}

export interface VertigripIntermediateFixationFormBody {
  intermediates: number | null;
  systemType: SystemTypeForSettings | null;
  shuttle: ShuttleForSettings | null;
  fixedInstallationType: boolean | null;
  installationType: InstallationTypeForSettings | null;
  intermediateElement: IntermediateElementForSettings | null;
  wallMaterial: WallMaterialForSettings | null;
  noWallMaterial: boolean;
  fixing: WallMaterialFixingForSettings | null;
}

export interface VertigripSupportsFormBody {
  upperFixation: VertigripUpperFixationFormBody;
  lowerFixation: VertigripLowerFixationFormBody;
  intermediateSupport: VertigripIntermediateFixationFormBody;
}

interface VertigripSupportsProps {
  defaultValues?: VertigripSupportsFormBody;
  settings?: VertigripSettings;
  steelVersion?: SteelVersionForSettings | null;
  heightToReach?: number | null;
  isLoading?: boolean;
  back: () => void;
  next: () => void;
  onSubmit: (body: VertigripSupportsFormBody) => Promise<void> | undefined;
  isAdmin?: boolean;
  canEdit?: boolean;
}

const VertigripSupports: FC<VertigripSupportsProps> = ({
  defaultValues,
  settings,
  steelVersion,
  heightToReach,
  isLoading,
  back,
  next,
  onSubmit,
  isAdmin,
  canEdit,
}: VertigripSupportsProps) => {
  const i18n = useI18n();

  const [showBaseHeightHelpModal, setShowBaseHeightHelpModal] = useState(false);
  const [showNrIntermediatesHelpModal, setShowNrIntermediatesHelpModal] = useState(false);
  const [showUpperInstallationTypesHelpModal, setShowUpperInstallationTypesHelpModal] = useState(false);
  const [showLowerInstallationTypesHelpModal, setShowLowerInstallationTypesHelpModal] = useState(false);
  const [showIntermediateInstallationTypesHelpModal, setShowIntermediateInstallationTypesHelpModal] = useState(false);
  const [showPassingThroughInstallationTypesHelpModal, setShowPassingThroughInstallationTypesHelpModal] =
    useState(false);
  const [showUpperExtensionsHelpModal, setShowUpperExtensionsHelpModal] = useState(false);
  const [showHandlesHelpModal, setShowHandlesHelpModal] = useState(false);
  const [showLadderReinforcementsHelpModal, setShowLadderReinforcementsHelpModal] = useState(false);
  const [showLowerElementsHelpModal, setShowLowerElementsHelpModal] = useState(false);
  const [showAbsorbersHelpModal, setShowAbsorbersHelpModal] = useState(false);
  const [showSystemTypesHelpModal, setShowSystemTypesHelpModal] = useState(false);
  const [showIntermediateElementsHelpModal, setShowIntermediateElementsHelpModal] = useState(false);
  const [showShuttlesHelpModal, setShowShuttlesHelpModal] = useState(false);
  const [showUpperWallMaterialsHelpModal, setShowUpperWallMaterialsHelpModal] = useState(false);
  const [showLowerWallMaterialsHelpModal, setShowLowerWallMaterialsHelpModal] = useState(false);
  const [showIntermediateWallMaterialsHelpModal, setShowIntermediateWallMaterialsHelpModal] = useState(false);
  const [showUpperWallMaterialFixingsHelpModal, setShowUpperWallMaterialFixingsHelpModal] = useState(false);
  const [showLowerWallMaterialFixingsHelpModal, setShowLowerWallMaterialFixingsHelpModal] = useState(false);
  const [showIntermediateWallMaterialFixingsHelpModal, setShowIntermediateWallMaterialFixingsHelpModal] =
    useState(false);

  const baseHeightOptions: BaseHeightOption[] = [{ value: 0.3 }, { value: 0.4 }, { value: 0.5 }, { value: 0.6 }];

  const fakeHandle: HandleForSettings = {
    id: "fakeId",
    code: i18n.translation.common.no,
    description: "",
  };

  const fakeLadderReinforcement: LadderReinforcementForSettings = {
    id: "fakeId",
    code: i18n.translation.common.no,
    description: "",
  };

  const fakeShuttle: ShuttleForSettings = {
    id: "fakeId",
    code: i18n.translation.shuttles.no,
    description: "",
  };

  const fakeWallMaterial: WallMaterialForSettings = {
    id: "fakeId",
    name: i18n.translation.wallMaterials.noWallMaterial,
    description: "",
    fixings: [],
  };

  const tmpValues = useRef({
    installationType: defaultValues?.intermediateSupport.installationType,
    intermediateElement: defaultValues?.intermediateSupport.intermediateElement,
  });

  const booleanValues = getBooleanValues(i18n.translation);

  return (
    <Form<VertigripSupportsFormBody>
      mode="onBlur"
      defaultValues={defaultValues}
      disabled={isLoading}
      fullWidth
      onSubmit={() => async (formData) =>
        isAdmin ? await onSubmit(formData) : canEdit ? await onSubmit(formData)?.then(() => next()) : next()
      }
      submitText={isAdmin ? i18n.translation.common.save : i18n.translation.common.next}
      secondaryButton={
        <>
          {isLoading && <LoadingSpinner delayed={0} size="small" />}
          <Button
            buttonProps={{
              disabled: isLoading,
              onClick: back,
            }}
            type="secondary"
          >
            {i18n.translation.common.back}
          </Button>
        </>
      }
    >
      {({ control, handleSubmit, reset, setValue, watch }) => {
        const submit = () =>
          !isAdmin &&
          canEdit &&
          handleSubmit((data) => {
            onSubmit(data)?.then(() => {
              reset(data);
            });
          })();

        useEffect(() => {
          if (defaultValues) {
            reset(defaultValues);
            tmpValues.current = {
              installationType: defaultValues.intermediateSupport.installationType,
              intermediateElement: defaultValues.intermediateSupport.intermediateElement,
            };
          }
        }, [defaultValues, reset]);

        const upperInstallationType = watch("upperFixation.installationType");
        const upperExtension = watch("upperFixation.upperExtension");
        const upperHandle = watch("upperFixation.handle");
        const upperLadderReinforcement = watch("upperFixation.ladderReinforcement");
        const upperWallMaterial = watch("upperFixation.wallMaterial");

        const baseHeight = watch("lowerFixation.baseHeight");
        const lowerInstallationType = watch("lowerFixation.installationType");
        const lowerWallMaterial = watch("lowerFixation.wallMaterial");
        const lowerNoWallMaterial = watch("lowerFixation.noWallMaterial");

        const intermediates = watch("intermediateSupport.intermediates");
        const systemType = watch("intermediateSupport.systemType");
        const fixedInstallationType = watch("intermediateSupport.fixedInstallationType");
        const intermediateInstallationType = watch("intermediateSupport.installationType");
        const intermediateWallMaterial = watch("intermediateSupport.wallMaterial");
        const intermediateNoWallMaterial = watch("intermediateSupport.noWallMaterial");

        const totalHeight = Math.max((heightToReach ?? 0) - (baseHeight?.value ?? 0), 0);
        const intermediateSpan = totalHeight / ((intermediates ?? 0) + 1);

        const getIntermediateElementOptions = (): IntermediateElementForSettings[] =>
          intermediateInstallationType?.intermediateElements ??
          systemType?.intermediateElements?.find(
            (item) => item.fixed === fixedInstallationType && steelVersion?.id === item.steelVersionId,
          )?.items ??
          [];

        return (
          <Sections columns={3} expandLastOnTablet>
            <SectionCard title={i18n.translation.common.upperSupport}>
              <Controller
                name="upperFixation.installationType"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const installationTypes = steelVersion?.installationTypes ?? [];

                  const updateInstallationType = (
                    value?: InstallationTypeForSettings | SingleValue<InstallationTypeForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      const installationType = installationTypes.find((item) => item.id === value?.id) ?? null;
                      const nextUpperExtension = installationType?.upperExtensions[0] ?? null;

                      setValue("upperFixation.installationType", installationType);
                      setValue("upperFixation.upperExtension", nextUpperExtension);
                      setValue("upperFixation.handle", nextUpperExtension?.handles[0] ? upperHandle : null);
                      setValue(
                        "upperFixation.ladderReinforcement",
                        installationType?.ladderReinforcements[0] ? upperLadderReinforcement : null,
                      );
                      setValue("upperFixation.wallMaterial", fakeWallMaterial);
                      setValue("upperFixation.fixing", null);

                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.installationTypes.singular}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowUpperInstallationTypesHelpModal(true),
                      }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={installationTypes}
                            isDisabled={
                              field.disabled ||
                              isLoading ||
                              !canEdit ||
                              (!!watch("upperFixation.installationType.id") && installationTypes.length < 2)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateInstallationType}
                          />
                          {showUpperInstallationTypesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.installationTypes.plural}
                              onClose={() => setShowUpperInstallationTypesHelpModal(false)}
                              isOpen={showUpperInstallationTypesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (installationType) => {
                                      updateInstallationType(
                                        installationTypes.find((item) => item.id === installationType.id),
                                      );
                                      setShowUpperInstallationTypesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={installationTypes.map((item) => ({
                                id: item.id,
                                title: item.name,
                                description: item.description,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="upperFixation.upperExtension"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const upperExtensions = upperInstallationType?.upperExtensions ?? [];

                  const updateUpperExtension = (value?: SingleValue<(typeof upperExtensions)[number]>) => {
                    if (value?.id !== field.value?.id) {
                      setValue("upperFixation.upperExtension", value ?? null);
                      setValue("upperFixation.handle", value?.handles[0] ? upperHandle : null);

                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.upperExtensions.singular}
                      error={fieldState.error}
                      showHelp={{ onOpen: isLoading ? undefined : () => setShowUpperExtensionsHelpModal(true) }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={upperExtensions}
                            isDisabled={
                              field.disabled ||
                              isLoading ||
                              !canEdit ||
                              (!!watch("upperFixation.upperExtension.id") && upperExtensions.length < 2)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.code}
                            onChange={updateUpperExtension}
                          />
                          {showUpperExtensionsHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.upperExtensions.plural}
                              onClose={() => setShowUpperExtensionsHelpModal(false)}
                              isOpen={showUpperExtensionsHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (upperExtension) => {
                                      updateUpperExtension(
                                        upperExtensions.find((item) => item.id === upperExtension.id) ?? null,
                                      );
                                      setShowUpperExtensionsHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={upperExtensions.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="upperFixation.handle"
                control={control}
                render={({ field, fieldState }) => {
                  const handles = upperExtension?.handles ?? [];

                  const updateHandle = (value?: HandleForSettings | SingleValue<HandleForSettings>) => {
                    if (value?.id !== (field.value ?? fakeHandle).id) {
                      setValue("upperFixation.handle", value?.id === fakeHandle.id ? null : (value ?? null));
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.handles.singular}
                      error={fieldState.error}
                      showHelp={
                        handles.length
                          ? { onOpen: isLoading ? undefined : () => setShowHandlesHelpModal(true) }
                          : undefined
                      }
                      isOptional
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={[fakeHandle, ...handles]}
                            isDisabled={field.disabled || isLoading || !canEdit || !handles.length}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) =>
                              item.id === fakeHandle.id ? i18n.translation.handles.noHandle : item.code
                            }
                            onChange={updateHandle}
                            value={handles.find((item) => item.id === field.value?.id) ?? fakeHandle}
                          />
                          {showHandlesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.handles.plural}
                              onClose={() => setShowHandlesHelpModal(false)}
                              isOpen={showHandlesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (selectedOption) => {
                                      updateHandle(handles.find((item) => item.id === selectedOption.id) ?? null);
                                      setShowHandlesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={handles.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="upperFixation.ladderReinforcement"
                control={control}
                render={({ field, fieldState }) => {
                  const ladderReinforcements = upperInstallationType?.ladderReinforcements ?? [];

                  const updateLadderReinforcement = (
                    value?: LadderReinforcementForSettings | SingleValue<LadderReinforcementForSettings>,
                  ) => {
                    if (value?.id !== (field.value ?? fakeLadderReinforcement).id) {
                      setValue(
                        "upperFixation.ladderReinforcement",
                        value?.id === fakeLadderReinforcement.id ? null : (value ?? null),
                      );
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.ladderReinforcements.singular}
                      hidden={!ladderReinforcements.length}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowLadderReinforcementsHelpModal(true),
                      }}
                      isOptional
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={[fakeLadderReinforcement, ...ladderReinforcements]}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) =>
                              item.id === fakeLadderReinforcement.id
                                ? i18n.translation.ladderReinforcements.noLadderReinforcement
                                : item.code
                            }
                            onChange={updateLadderReinforcement}
                            value={
                              ladderReinforcements.find((item) => item.id === field.value?.id) ??
                              fakeLadderReinforcement
                            }
                          />
                          {showLadderReinforcementsHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.ladderReinforcements.plural}
                              onClose={() => setShowLadderReinforcementsHelpModal(false)}
                              isOpen={showLadderReinforcementsHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (selectedOption) => {
                                      updateLadderReinforcement(
                                        ladderReinforcements.find((item) => item.id === selectedOption.id) ?? null,
                                      );
                                      setShowLadderReinforcementsHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={ladderReinforcements.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="upperFixation.wallMaterial"
                control={control}
                render={({ field, fieldState }) => {
                  const wallMaterials = upperInstallationType?.wallMaterials ?? [];

                  const updateWallMaterial = (
                    value?: WallMaterialForSettings | SingleValue<WallMaterialForSettings>,
                  ) => {
                    if ((value ?? fakeWallMaterial).id !== (field.value ?? fakeWallMaterial).id) {
                      const wallMaterial = value ?? fakeWallMaterial;
                      setValue("upperFixation.wallMaterial", wallMaterial);
                      setValue(
                        "upperFixation.fixing",
                        wallMaterial.id === fakeWallMaterial.id ? null : (wallMaterial.fixings[0] ?? null),
                      );
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.wallMaterials.singular}
                      hidden={!wallMaterials.length}
                      error={fieldState.error}
                      showHelp={{ onOpen: isLoading ? undefined : () => setShowUpperWallMaterialsHelpModal(true) }}
                      isOptional
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={[fakeWallMaterial, ...wallMaterials]}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateWallMaterial}
                            value={wallMaterials.find((item) => item.id === field.value?.id) ?? fakeWallMaterial}
                          />
                          <WallMaterialsHelpModal
                            onClose={() => setShowUpperWallMaterialsHelpModal(false)}
                            isOpen={showUpperWallMaterialsHelpModal}
                            onSelect={
                              isAdmin || canEdit
                                ? (selectedOption) => {
                                    updateWallMaterial(
                                      wallMaterials.find((item) => item.id === selectedOption.id) ?? null,
                                    );
                                    setShowUpperWallMaterialsHelpModal(false);
                                  }
                                : undefined
                            }
                            items={wallMaterials}
                          />
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="upperFixation.fixing"
                control={control}
                render={({ field, fieldState }) => {
                  const fixingDisabled = !upperWallMaterial?.fixings[0];

                  const updateFixing = (
                    value?: WallMaterialFixingForSettings | SingleValue<WallMaterialFixingForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      setValue("upperFixation.fixing", value ?? null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.wallMaterialFixings.singular}
                      hidden={
                        !upperInstallationType?.wallMaterials.length ||
                        !watch("upperFixation.wallMaterial.id") ||
                        watch("upperFixation.wallMaterial.id") === fakeWallMaterial.id
                      }
                      disabled={fixingDisabled}
                      error={fieldState.error}
                      showHelp={
                        fixingDisabled
                          ? undefined
                          : { onOpen: isLoading ? undefined : () => setShowUpperWallMaterialFixingsHelpModal(true) }
                      }
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={upperWallMaterial?.fixings ?? []}
                            isDisabled={field.disabled || fixingDisabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateFixing}
                          />
                          <WallMaterialFixingsHelpModal
                            onClose={() => setShowUpperWallMaterialFixingsHelpModal(false)}
                            isOpen={showUpperWallMaterialFixingsHelpModal}
                            onSelect={
                              isAdmin || canEdit
                                ? (selectedOption) => {
                                    updateFixing(
                                      upperWallMaterial?.fixings.find((item) => item.id === selectedOption.id) ??
                                        null,
                                    );
                                    setShowUpperWallMaterialFixingsHelpModal(false);
                                  }
                                : undefined
                            }
                            items={upperWallMaterial?.fixings ?? []}
                          />
                        </>
                      )}
                    </FormField>
                  );
                }}
              />
            </SectionCard>

            <SectionCard title={i18n.translation.common.lowerSupport}>
              <Controller
                name="lowerFixation.baseHeight"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={`${i18n.translation.common.baseHeight} (Hb)`}
                    error={fieldState.error}
                    showHelp={
                      settings?.texts.baseHeight
                        ? { onOpen: isLoading ? undefined : () => setShowBaseHeightHelpModal(true) }
                        : undefined
                    }
                  >
                    {({ labelId }) => (
                      <>
                        <CustomSelect
                          {...field}
                          inputId={labelId}
                          options={baseHeightOptions}
                          isDisabled={field.disabled || isLoading || !canEdit}
                          isLoading={isLoading}
                          getOptionValue={(item) => item.value.toString()}
                          getOptionLabel={(item) => `${item.value} m`}
                          onChange={(value) => {
                            setValue(
                              "lowerFixation.baseHeight",
                              baseHeightOptions.find((item) => item.value === value?.value),
                            );
                            submit();
                          }}
                        />
                        {showBaseHeightHelpModal && settings?.texts.baseHeight && (
                          <SimpleTextModalDialog
                            title={i18n.translation.common.baseHeight}
                            text={settings.texts.baseHeight}
                            isOpen={showBaseHeightHelpModal}
                            onClose={() => setShowBaseHeightHelpModal(false)}
                          />
                        )}
                      </>
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="lowerFixation.installationType"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const installationTypes = steelVersion?.installationTypes ?? [];

                  const updateInstallationType = (
                    value?: InstallationTypeForSettings | SingleValue<InstallationTypeForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      const installationType = installationTypes.find((item) => item.id === value?.id) ?? null;
                      setValue("lowerFixation.installationType", installationType);
                      setValue("lowerFixation.lowerElement", installationType?.lowerElements[0] ?? null);
                      setValue("lowerFixation.wallMaterial", fakeWallMaterial);
                      setValue("lowerFixation.fixing", null);
                      setValue("lowerFixation.noWallMaterial", true);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.installationTypes.singular}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowLowerInstallationTypesHelpModal(true),
                      }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={installationTypes}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateInstallationType}
                          />
                          {showLowerInstallationTypesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.installationTypes.plural}
                              onClose={() => setShowLowerInstallationTypesHelpModal(false)}
                              isOpen={showLowerInstallationTypesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (installationType) => {
                                      updateInstallationType(
                                        installationTypes.find((item) => item.id === installationType.id) ?? null,
                                      );
                                      setShowLowerInstallationTypesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={installationTypes.map((item) => ({
                                id: item.id,
                                title: item.name,
                                description: item.description,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="lowerFixation.lowerElement"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const lowerElements = lowerInstallationType?.lowerElements ?? [];

                  const updateLowerElement = (value?: SingleValue<(typeof lowerElements)[number]>) => {
                    if (value?.id !== field.value?.id) {
                      setValue("lowerFixation.lowerElement", value ?? null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.lowerElements.singular}
                      error={fieldState.error}
                      showHelp={{ onOpen: isLoading ? undefined : () => setShowLowerElementsHelpModal(true) }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={lowerElements}
                            isDisabled={
                              field.disabled ||
                              isLoading ||
                              !canEdit ||
                              (!!watch("lowerFixation.lowerElement.id") && lowerElements.length < 2)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.code}
                            onChange={updateLowerElement}
                          />
                          {showLowerElementsHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.lowerElements.plural}
                              onClose={() => setShowLowerElementsHelpModal(false)}
                              isOpen={showLowerElementsHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (lowerElement) => {
                                      updateLowerElement(
                                        lowerElements.find((item) => item.id === lowerElement.id) ?? null,
                                      );
                                      setShowLowerElementsHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={lowerElements.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="lowerFixation.absorber"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const absorbers = steelVersion?.absorbers ?? [];

                  const updateAbsorber = (value?: SingleValue<(typeof absorbers)[number]>) => {
                    if (value?.id !== field.value?.id) {
                      setValue("lowerFixation.absorber", value ?? null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.absorbers.singular}
                      error={fieldState.error}
                      showHelp={{ onOpen: isLoading ? undefined : () => setShowAbsorbersHelpModal(true) }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={absorbers}
                            isDisabled={
                              field.disabled ||
                              isLoading ||
                              !canEdit ||
                              (!!watch("lowerFixation.absorber.id") && absorbers.length < 2)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.code}
                            onChange={updateAbsorber}
                          />
                          {showAbsorbersHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.absorbers.plural}
                              onClose={() => setShowAbsorbersHelpModal(false)}
                              isOpen={showAbsorbersHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (absorber) => {
                                      updateAbsorber(absorbers.find((item) => item.id === absorber.id) ?? null);
                                      setShowAbsorbersHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={absorbers.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="lowerFixation.wallMaterial"
                control={control}
                render={({ field, fieldState }) => {
                  const wallMaterials = lowerInstallationType?.wallMaterials ?? [];

                  const updateWallMaterial = (
                    value?: WallMaterialForSettings | SingleValue<WallMaterialForSettings>,
                  ) => {
                    const wallMaterial = value ?? fakeWallMaterial;

                    if (wallMaterial.id !== (field.value ?? fakeWallMaterial).id) {
                      setValue("lowerFixation.noWallMaterial", wallMaterial.id === fakeWallMaterial.id);
                      setValue("lowerFixation.wallMaterial", wallMaterial);
                      setValue(
                        "lowerFixation.fixing",
                        wallMaterial.id === fakeWallMaterial.id ? null : (wallMaterial.fixings[0] ?? null),
                      );
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.wallMaterials.singular}
                      hidden={!wallMaterials.length}
                      error={fieldState.error}
                      showHelp={{ onOpen: isLoading ? undefined : () => setShowLowerWallMaterialsHelpModal(true) }}
                      isOptional
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={[fakeWallMaterial, ...wallMaterials]}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateWallMaterial}
                            value={lowerWallMaterial ?? (lowerNoWallMaterial ? fakeWallMaterial : null)}
                          />
                          <WallMaterialsHelpModal
                            onClose={() => setShowLowerWallMaterialsHelpModal(false)}
                            isOpen={showLowerWallMaterialsHelpModal}
                            onSelect={
                              isAdmin || canEdit
                                ? (selectedOption) => {
                                    updateWallMaterial(
                                      wallMaterials.find((item) => item.id === selectedOption.id) ?? null,
                                    );
                                    setShowLowerWallMaterialsHelpModal(false);
                                  }
                                : undefined
                            }
                            items={wallMaterials}
                          />
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="lowerFixation.fixing"
                control={control}
                render={({ field, fieldState }) => {
                  const fixingDisabled =
                    !lowerWallMaterial?.fixings[0] || lowerWallMaterial?.id === fakeWallMaterial.id;

                  const updateFixing = (
                    value?: WallMaterialFixingForSettings | SingleValue<WallMaterialFixingForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      setValue("lowerFixation.fixing", value ?? null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.wallMaterialFixings.singular}
                      hidden={
                        !lowerInstallationType?.wallMaterials.length ||
                        !watch("lowerFixation.wallMaterial.id") ||
                        watch("lowerFixation.wallMaterial.id") === fakeWallMaterial.id
                      }
                      disabled={fixingDisabled}
                      error={fieldState.error}
                      showHelp={
                        fixingDisabled
                          ? undefined
                          : { onOpen: isLoading ? undefined : () => setShowLowerWallMaterialFixingsHelpModal(true) }
                      }
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={lowerWallMaterial?.fixings ?? []}
                            isDisabled={field.disabled || fixingDisabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateFixing}
                          />
                          <WallMaterialFixingsHelpModal
                            onClose={() => setShowLowerWallMaterialFixingsHelpModal(false)}
                            isOpen={showLowerWallMaterialFixingsHelpModal}
                            onSelect={
                              isAdmin || canEdit
                                ? (selectedOption) => {
                                    updateFixing(
                                      lowerWallMaterial?.fixings.find((item) => item.id === selectedOption.id) ??
                                        null,
                                    );
                                    setShowLowerWallMaterialFixingsHelpModal(false);
                                  }
                                : undefined
                            }
                            items={lowerWallMaterial?.fixings ?? []}
                          />
                        </>
                      )}
                    </FormField>
                  );
                }}
              />
            </SectionCard>

            <SectionCard title={i18n.translation.common.intermediateSupport}>
              <Controller
                name="intermediateSupport.intermediates"
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.translation.common.nrIntermediatesAndSpan}
                    error={fieldState.error}
                    showHelp={
                      settings?.texts.intermediatesAndSpan
                        ? { onOpen: isLoading ? undefined : () => setShowNrIntermediatesHelpModal(true) }
                        : undefined
                    }
                  >
                    {({ labelId, isInvalid }) => (
                      <div className={styles.intermediates}>
                        <Numeric
                          {...field}
                          id={labelId}
                          min={0}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          disabled={field.disabled || isLoading}
                          readOnly={!canEdit}
                          onBlur={(value) => {
                            if (value !== field.value) {
                              setValue("intermediateSupport.intermediates", value);
                              submit();
                            }
                          }}
                          controls={
                            canEdit
                              ? {
                                  onUpdate: (value) => {
                                    setValue("intermediateSupport.intermediates", value);
                                    !isAdmin && submit();
                                  },
                                  scale: 1,
                                }
                              : undefined
                          }
                        />
                        {showNrIntermediatesHelpModal && settings?.texts.intermediatesAndSpan && (
                          <SimpleTextModalDialog
                            title={i18n.translation.common.nrIntermediatesAndSpan}
                            text={settings.texts.intermediatesAndSpan}
                            isOpen={showNrIntermediatesHelpModal}
                            onClose={() => setShowNrIntermediatesHelpModal(false)}
                          />
                        )}
                        <Numeric
                          readOnly
                          decimalScale={2}
                          isInvalid={intermediateSpan >= 5}
                          disabled={field.disabled || isLoading}
                          suffix=" m"
                          value={isLoading || !intermediates ? "" : intermediateSpan}
                        />
                      </div>
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="intermediateSupport.systemType"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const systemTypes = settings?.systemTypes ?? [];

                  const updateSystemType = (value?: SystemTypeForSettings | SingleValue<SystemTypeForSettings>) => {
                    if (value?.id !== field.value?.id) {
                      const nextSystemType = value ?? null;
                      setValue("intermediateSupport.systemType", nextSystemType);
                      setValue("intermediateSupport.shuttle", nextSystemType?.shuttles[0] ?? null);

                      if (nextSystemType?.isPassingThrough) {
                        const nextFixedInstallationType = true;
                        setValue("intermediateSupport.fixedInstallationType", nextFixedInstallationType);
                        setValue("intermediateSupport.installationType", null);
                        setValue(
                          "intermediateSupport.intermediateElement",
                          nextSystemType?.intermediateElements?.find(
                            (item) =>
                              item.fixed === nextFixedInstallationType && steelVersion?.id === item.steelVersionId,
                          )?.items[0] ?? null,
                        );
                      } else {
                        const nextInstallationType =
                          steelVersion?.installationTypes.find(
                            (item) => item.id === tmpValues.current.installationType?.id,
                          ) ??
                          lowerInstallationType ??
                          null;

                        setValue("intermediateSupport.fixedInstallationType", null);
                        setValue("intermediateSupport.installationType", nextInstallationType);
                        setValue(
                          "intermediateSupport.intermediateElement",
                          nextInstallationType?.intermediateElements[0] ?? null,
                        );
                      }

                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.systemTypes.singular}
                      error={fieldState.error}
                      showHelp={{ onOpen: isLoading ? undefined : () => setShowSystemTypesHelpModal(true) }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={systemTypes}
                            isDisabled={
                              field.disabled || isLoading || !canEdit || (systemTypes.length < 2 && !!field.value)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateSystemType}
                          />
                          {showSystemTypesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.systemTypes.plural}
                              onClose={() => setShowSystemTypesHelpModal(false)}
                              isOpen={showSystemTypesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (systemTypeItem) => {
                                      updateSystemType(
                                        systemTypes.find((item) => item.id === systemTypeItem.id) ?? null,
                                      );
                                      setShowSystemTypesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={systemTypes.map((item) => ({
                                id: item.id,
                                title: item.name,
                                description: item.description,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="intermediateSupport.fixedInstallationType"
                control={control}
                render={({ field, fieldState }) => {
                  const updateFixedInstallationType = (
                    value?: BooleanSelectValue | SingleValue<BooleanSelectValue>,
                  ) => {
                    if (value?.value !== field.value) {
                      setValue("intermediateSupport.fixedInstallationType", value?.value === true);
                      setValue(
                        "intermediateSupport.intermediateElement",
                        systemType?.intermediateElements?.find(
                          (item) => item.fixed === value?.value && steelVersion?.id === item.steelVersionId,
                        )?.items[0] ?? null,
                      );
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.installationTypes.singular}
                      hidden={!systemType?.isPassingThrough}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowPassingThroughInstallationTypesHelpModal(true),
                      }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={booleanValues}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.value.toString()}
                            getOptionLabel={(item) =>
                              item.value === true ? i18n.translation.common.fixed : i18n.translation.common.removable
                            }
                            onChange={updateFixedInstallationType}
                            value={booleanValues.find((item) => item.value === field.value) ?? null}
                          />
                          {showPassingThroughInstallationTypesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.installationTypes.plural}
                              onClose={() => setShowPassingThroughInstallationTypesHelpModal(false)}
                              isOpen={showPassingThroughInstallationTypesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (selectedOption) => {
                                      updateFixedInstallationType(
                                        booleanValues.find((item) => `${item.value}` === selectedOption.id) ?? null,
                                      );
                                      setShowPassingThroughInstallationTypesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={booleanValues.map((item) => ({
                                id: `${item.value}`,
                                title:
                                  item.value === true
                                    ? i18n.translation.common.fixed
                                    : i18n.translation.common.removable,
                                description: "",
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="intermediateSupport.installationType"
                control={control}
                rules={{ required: !systemType?.isPassingThrough }}
                render={({ field, fieldState }) => {
                  const installationTypes = steelVersion?.installationTypes ?? [];

                  const updateInstallationType = (
                    value?: InstallationTypeForSettings | SingleValue<InstallationTypeForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      const installationType = installationTypes.find((item) => item.id === value?.id) ?? null;
                      const intermediateElement = installationType?.intermediateElements[0] ?? null;

                      setValue("intermediateSupport.installationType", installationType);
                      setValue("intermediateSupport.intermediateElement", intermediateElement);
                      setValue("intermediateSupport.wallMaterial", fakeWallMaterial);
                      setValue("intermediateSupport.fixing", null);
                      setValue("intermediateSupport.noWallMaterial", true);

                      tmpValues.current = {
                        installationType,
                        intermediateElement,
                      };

                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.installationTypes.singular}
                      hidden={!!systemType?.isPassingThrough}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowIntermediateInstallationTypesHelpModal(true),
                      }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={installationTypes}
                            isDisabled={field.disabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateInstallationType}
                          />
                          {showIntermediateInstallationTypesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.installationTypes.plural}
                              onClose={() => setShowIntermediateInstallationTypesHelpModal(false)}
                              isOpen={showIntermediateInstallationTypesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (installationType) => {
                                      updateInstallationType(
                                        installationTypes.find((item) => item.id === installationType.id) ?? null,
                                      );
                                      setShowIntermediateInstallationTypesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={installationTypes.map((item) => ({
                                id: item.id,
                                title: item.name,
                                description: item.description,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="intermediateSupport.intermediateElement"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  const options = getIntermediateElementOptions();

                  const updateIntermediateElement = (
                    value?: IntermediateElementForSettings | SingleValue<IntermediateElementForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      setValue("intermediateSupport.intermediateElement", value ?? null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.intermediateElements.singular}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowIntermediateElementsHelpModal(true),
                      }}
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={options}
                            isDisabled={
                              field.disabled || isLoading || !canEdit || (options.length < 2 && !!field.value)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.code}
                            onChange={updateIntermediateElement}
                          />
                          {showIntermediateElementsHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.intermediateElements.plural}
                              onClose={() => setShowIntermediateElementsHelpModal(false)}
                              isOpen={showIntermediateElementsHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (selectedOption) => {
                                      updateIntermediateElement(
                                        options.find((item) => item.id === selectedOption.id) ?? null,
                                      );
                                      setShowIntermediateElementsHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={options.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="intermediateSupport.shuttle"
                control={control}
                render={({ field, fieldState }) => {
                  const shuttles = systemType?.shuttles ?? [];

                  const updateShuttle = (value?: ShuttleForSettings | SingleValue<ShuttleForSettings>) => {
                    if (value?.id !== (field.value ?? fakeShuttle).id) {
                      setValue("intermediateSupport.shuttle", value && value.id !== fakeShuttle.id ? value : null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.shuttles.singular}
                      error={fieldState.error}
                      showHelp={
                        shuttles.length
                          ? { onOpen: isLoading ? undefined : () => setShowShuttlesHelpModal(true) }
                          : undefined
                      }
                      isOptional
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={[fakeShuttle, ...shuttles]}
                            isDisabled={field.disabled || isLoading || !canEdit || !shuttles.length}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.code}
                            onChange={updateShuttle}
                            value={shuttles.find((item) => item.id === field.value?.id) ?? fakeShuttle}
                          />
                          {showShuttlesHelpModal && (
                            <ItemsHelpModal
                              title={i18n.translation.shuttles.plural}
                              onClose={() => setShowShuttlesHelpModal(false)}
                              isOpen={showShuttlesHelpModal}
                              onSelect={
                                isAdmin || canEdit
                                  ? (shuttle) => {
                                      updateShuttle(shuttles.find((item) => item.id === shuttle.id) ?? null);
                                      setShowShuttlesHelpModal(false);
                                    }
                                  : undefined
                              }
                              items={shuttles.map((item) => ({
                                id: item.id,
                                title: item.code,
                                description: item.description,
                                name: item.name,
                                image: item.image,
                              }))}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="intermediateSupport.wallMaterial"
                control={control}
                render={({ field, fieldState }) => {
                  const wallMaterials = intermediateInstallationType?.wallMaterials ?? [];

                  const updateWallMaterial = (
                    value?: WallMaterialForSettings | SingleValue<WallMaterialForSettings>,
                  ) => {
                    const wallMaterial = value ?? fakeWallMaterial;

                    if (wallMaterial.id !== (field.value ?? fakeWallMaterial).id) {
                      setValue("intermediateSupport.noWallMaterial", wallMaterial.id === fakeWallMaterial.id);
                      setValue("intermediateSupport.wallMaterial", wallMaterial);
                      setValue(
                        "intermediateSupport.fixing",
                        wallMaterial.id === fakeWallMaterial.id ? null : (wallMaterial.fixings[0] ?? null),
                      );
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.wallMaterials.singular}
                      hidden={!wallMaterials.length}
                      error={fieldState.error}
                      showHelp={{
                        onOpen: isLoading ? undefined : () => setShowIntermediateWallMaterialsHelpModal(true),
                      }}
                      isOptional
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={[fakeWallMaterial, ...wallMaterials]}
                            isDisabled={
                              field.disabled || isLoading || !canEdit || (wallMaterials.length < 2 && !!field.value)
                            }
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateWallMaterial}
                            value={intermediateWallMaterial ?? (intermediateNoWallMaterial ? fakeWallMaterial : null)}
                          />
                          <WallMaterialsHelpModal
                            onClose={() => setShowIntermediateWallMaterialsHelpModal(false)}
                            isOpen={showIntermediateWallMaterialsHelpModal}
                            onSelect={
                              isAdmin || canEdit
                                ? (selectedOption) => {
                                    updateWallMaterial(
                                      wallMaterials.find((item) => item.id === selectedOption.id) ?? null,
                                    );
                                    setShowIntermediateWallMaterialsHelpModal(false);
                                  }
                                : undefined
                            }
                            items={wallMaterials}
                          />
                        </>
                      )}
                    </FormField>
                  );
                }}
              />

              <Controller
                name="intermediateSupport.fixing"
                control={control}
                render={({ field, fieldState }) => {
                  const fixingDisabled =
                    !intermediateWallMaterial?.fixings[0] || intermediateWallMaterial?.id === fakeWallMaterial.id;

                  const updateFixing = (
                    value?: WallMaterialFixingForSettings | SingleValue<WallMaterialFixingForSettings>,
                  ) => {
                    if (value?.id !== field.value?.id) {
                      setValue("intermediateSupport.fixing", value ?? null);
                      submit();
                    }
                  };

                  return (
                    <FormField
                      label={i18n.translation.wallMaterialFixings.singular}
                      hidden={
                        !intermediateInstallationType?.wallMaterials.length ||
                        !watch("intermediateSupport.wallMaterial.id") ||
                        watch("intermediateSupport.wallMaterial.id") === fakeWallMaterial.id
                      }
                      disabled={fixingDisabled}
                      error={fieldState.error}
                      showHelp={
                        fixingDisabled
                          ? undefined
                          : {
                              onOpen: isLoading
                                ? undefined
                                : () => setShowIntermediateWallMaterialFixingsHelpModal(true),
                            }
                      }
                    >
                      {({ labelId }) => (
                        <>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={intermediateWallMaterial?.fixings ?? []}
                            isDisabled={field.disabled || fixingDisabled || isLoading || !canEdit}
                            isLoading={isLoading}
                            getOptionValue={(item) => item.id}
                            getOptionLabel={(item) => item.name}
                            onChange={updateFixing}
                          />
                          <WallMaterialFixingsHelpModal
                            onClose={() => setShowIntermediateWallMaterialFixingsHelpModal(false)}
                            isOpen={showIntermediateWallMaterialFixingsHelpModal}
                            onSelect={
                              isAdmin || canEdit
                                ? (selectedOption) => {
                                    updateFixing(
                                      intermediateWallMaterial?.fixings.find(
                                        (item) => item.id === selectedOption.id,
                                      ) ?? null,
                                    );
                                    setShowIntermediateWallMaterialFixingsHelpModal(false);
                                  }
                                : undefined
                            }
                            items={intermediateWallMaterial?.fixings ?? []}
                          />
                        </>
                      )}
                    </FormField>
                  );
                }}
              />
            </SectionCard>
          </Sections>
        );
      }}
    </Form>
  );
};

export default VertigripSupports;
